/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import {
	View,
	BoxView,
	ActionButton,
	Radiobutton,
	Tooltip,
	BoxEmptyView,
} from '@components/atoms'
import {
	AccessControlView,
	AccessGroupUserList,
	AccessGroupTab,
	AccessControlSideModal,
	AccessGroupList,
} from '@components/systems'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'
import { motion } from 'framer-motion'

import {
	useAppSelector,
	useAppDispatch,
	accessControlActions,
	ApiGetDataStatus,
} from '@redux/index'
import { AccessControlType, AccessGroupType } from '@schemas'

/**
 * 권한 관리 — 좌 그룹/사원 탭 · 우 (그룹 리스트 |) 메뉴 권한 | 그룹 멤버. 세 칸은 헤어라인으로만 나뉜다.
 * 탭 전환 슬라이드(framer-motion)와 사이드 모달은 그대로.
 */
const AccessControlPage = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const selectedTab: string = useAppSelector(state => state.accessControl.selectedTab)
	const groupStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.groupAccessControlStatus,
	)
	const userStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.userAccessControlStatus,
	)
	const selectedAccessGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)
	const selectedAccessGroupByUserTab: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroupByUserTab,
	)
	const selectedGroupAccessControl: AccessControlType[] = useAppSelector(
		state => state.accessControl.selectedGroupAccessControl,
	)
	const selectedGroupAccessControlByUserTab: AccessControlType[] = useAppSelector(
		state => state.accessControl.selectedGroupAccessControlByUserTab,
	)
	const groupListByUserTab: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupListByUserTab,
	)
	const groupListStatusByUserTab: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.groupListStatusByUserTab,
	)

	// variables
	const groupMemberStatus: ApiGetDataStatus =
		(selectedTab === 'group' && groupStatus === 'loading') ||
		(selectedTab === 'user' && userStatus === 'loading')
			? 'loading'
			: selectedAccessGroup.users.length > 0
			? 'ok'
			: selectedAccessGroup.users.length === 0
			? 'empty'
			: groupStatus

	const onGroupByUserTabClick = (aGroup: AccessGroupType) => {
		reduxDispatch(
			accessControlActions.SELECTED_ACCESS_GROUP_BY_USER_TAB({
				...aGroup,
			}),
		)
	}

	const onEditGroups = () => {
		reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(true))
	}

	const onJoinGropClick = () => {
		reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(true))
	}

	return (
		<View css={style}>
			<View className="groupView">
				<AccessGroupTab />
			</View>

			<View className="accessView">
				<motion.div
					className="groupListAnimatedView"
					initial={false}
					animate={
						selectedTab === 'user' ? { opacity: 1, x: 0 } : { opacity: 0, x: '-100rem' }
					}
					exit={{ opacity: 0, x: '-100rem' }}
					transition={{ duration: 0.3 }}
				>
					<BoxView
						title="그룹 리스트"
						className="accessGroupListView"
						rightHeaderAccessory={
							<Tooltip title="그룹 수정">
								<ActionButton onClick={onEditGroups} iconName="cog" />
							</Tooltip>
						}
					>
						{groupListStatusByUserTab === 'empty' ? (
							<View className="accessGroupListEmptyView">
								<BoxEmptyView
									isGrey={true}
									title="연결된 그룹이 없습니다."
									subtitle="아래 + 버튼을 눌러서 추가해주세요."
								/>
								<View className="addButtonOuter">
									<button
										type="button"
										className="boBtnGhost addItem"
										onClick={onJoinGropClick}
									>
										<span className="mdi mdi-plus" aria-hidden="true" />
										그룹 추가
									</button>
								</View>
							</View>
						) : (
							<AccessGroupList
								groupList={groupListByUserTab}
								status={groupListStatusByUserTab}
								onGroupClick={onGroupByUserTabClick}
								selectedGroupIds={[selectedAccessGroupByUserTab.id]}
								rightAccessory={(group, isChecked) => {
									return (
										<Radiobutton
											value={group.id}
											selectedValue={isChecked ? group.id : 'none'}
											color={mono.ink}
										/>
									)
								}}
							/>
						)}
					</BoxView>
				</motion.div>

				<motion.div
					className="accessMenuAnimationView"
					initial={false}
					animate={selectedTab === 'group' ? { x: 0 } : { x: '20rem' }}
					exit={{ x: '100%' }}
					transition={{ duration: 0.3 }}
				>
					<BoxView title="메뉴 관리" className="accessMenuView">
						<AccessControlView
							status={selectedTab === 'group' ? groupStatus : userStatus}
							accessControlList={
								selectedTab === 'group'
									? selectedGroupAccessControl
									: selectedGroupAccessControlByUserTab
							}
							isSelectedGroup={selectedAccessGroupByUserTab.id !== ''}
							detailItemStyle={{ paddingLeft: '10rem' }}
							isShownDeleteButton={false}
						/>
					</BoxView>
				</motion.div>

				<motion.div
					className="accessGroupUserListAnimatedView"
					initial={false}
					animate={
						selectedTab === 'group' ? { opacity: 1, x: 0 } : { opacity: 0, x: '100rem' }
					}
					exit={{ opacity: 0, right: -200 }}
					transition={{ duration: 0.3 }}
				>
					<BoxView className="accessGroupUserListView" title="그룹 멤버 관리">
						<AccessGroupUserList
							status={groupMemberStatus}
							userList={selectedAccessGroup.users}
						/>
					</BoxView>
				</motion.div>
			</View>

			<AccessControlSideModal />
		</View>
	)
}

export default AccessControlPage

const style = css({
	...boShared,
	position: 'relative',
	display: 'flex',
	height: '100%',

	'.groupView': {
		width: '22rem',
		flexShrink: 0,
		borderRight: `1px solid ${mono.line}`,
	},

	'.accessView': {
		position: 'relative',
		display: 'flex',
		flex: 1,
		minWidth: 0,
		height: '100%',
		overflow: 'hidden',

		'.groupListAnimatedView': {
			position: 'absolute',
			width: '20rem',
			height: '100%',
			borderRight: `1px solid ${mono.line}`,

			'.accessGroupListView': {
				width: '20rem',

				'.boxChildrenView': {
					position: 'relative',
					overflow: 'hidden',

					'.accessGroupListEmptyView': {
						display: 'flex',
						flexDirection: 'column',
						width: '100%',
						height: '100%',

						'.addButtonOuter': {
							display: 'flex',
							justifyContent: 'center',
							paddingBottom: 24,

							'.addItem': {
								gap: 6,
								'.mdi': { fontSize: 16 },
							},
						},
					},
				},
			},
		},

		'.accessMenuAnimationView': {
			position: 'relative',
			width: 'calc(100% - 20rem)',
			maxWidth: '40rem',
			height: '100%',
			borderRight: `1px solid ${mono.line}`,

			'.accessMenuView': {
				width: '100%',
			},
		},

		'.accessGroupUserListAnimatedView': {
			position: 'relative',
			width: '20rem',
			height: '100%',

			'.accessGroupUserListView': {
				width: '100%',
			},
		},
	},
})
